import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GraphQLTimesheetService } from '../../services/graphqlTimesheet';
import { WeeklyTimesheet, TimesheetStatus, TimeEntry, formatWeekLabel, getLastNWeeks } from '../../types/timesheet';
import { UserRole } from '../../types/user';
import { notifyTimesheetApproved, notifyTimesheetReturned } from '../../services/inAppNotification';
import { emailTimesheetApproved, emailTimesheetReturned } from '../../services/emailNotification';
import { loadAllUsers, loadDirectReportsWithStatus } from '../../services/userLoader';
import { canonicalizeEmail } from '../../services/identity';
import InlineLoader from '../common/InlineLoader';
import EmptyState from '../common/EmptyState';
import './TeamTimesheets.css';

type FilterKey = 'All' | TimesheetStatus;
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'All', label: 'All' },
  { key: TimesheetStatus.SUBMITTED, label: 'Pending' },
  { key: TimesheetStatus.APPROVED, label: 'Approved' },
  { key: TimesheetStatus.RETURNED, label: 'Returned' },
];

const TeamTimesheets: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [timesheets, setTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [filter, setFilter] = useState<FilterKey>('All');
  const [weekFilter, setWeekFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState<{ name: string; email: string }[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [entries, setEntries] = useState<Record<string, TimeEntry[]>>({});
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<{ name: string; email: string }[]>([]);
  /*
   * Set when the DIRECTORY could not be read, as opposed to a manager genuinely having no team.
   *
   * Both arrive as an empty report list, and scoping the table through an empty list turns a
   * failed listUsers call into "your team has submitted nothing" — a manager would believe it
   * and stop looking. loadDirectReportsWithStatus exists to tell the two apart; this surfaces it.
   */
  const [directoryFailed, setDirectoryFailed] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const DEBUG = process.env.REACT_APP_DEBUG_TIMESHEET === 'true';
    try {
      if (DEBUG) {
        console.log('[Timesheet Debug] Load started', {
          role: user.role,
          timestamp: new Date().toISOString()
        });
      }

      const isAdminRole =
        user.role === UserRole.SUPER_ADMIN ||
        user.role === UserRole.HR_ADMIN ||
        user.role === UserRole.FINANCE_ADMIN;

      /*
       * The reporting line is fetched HERE, alongside the timesheets, rather than in its own
       * effect.
       *
       * Visibility now depends on it, so the two cannot be allowed to race: loaded separately,
       * the table would render once against an empty team and show a manager nothing before
       * correcting itself, which reads as "no submissions" for as long as it lasts.
       */
      const [submitted, approved, returned, team] = await Promise.all([
        GraphQLTimesheetService.getTimesheetsByStatus(TimesheetStatus.SUBMITTED),
        GraphQLTimesheetService.getTimesheetsByStatus(TimesheetStatus.APPROVED),
        GraphQLTimesheetService.getTimesheetsByStatus(TimesheetStatus.RETURNED),
        isAdminRole
          ? Promise.resolve(null)
          : loadDirectReportsWithStatus(user.email).catch(() => ({
              directoryAvailable: false,
              reports: [] as { name: string; email: string }[],
            })),
      ]);

      setDirectoryFailed(Boolean(team && !team.directoryAvailable));

      if (team) {
        setAllUsers(team.reports.map(u => ({ name: u.name, email: u.email })));
      } else {
        const everyone = await loadAllUsers().catch(() => []);
        setAllUsers(everyone.map(u => ({ name: u.name, email: u.email })));
      }

      /*
       * Who this manager may see: their CURRENT reports, by identity rather than by string.
       *
       * loadDirectReportsWithStatus matches on sameIdentity, so a report listed under either
       * company domain is found; canonicalising the timesheet's employeeId here closes the same
       * gap on the other side of the comparison.
       */
      const reportEmails = new Set(
        (team?.reports || []).map(u => canonicalizeEmail(u.email)).filter(Boolean)
      );

      if (DEBUG) {
        console.log('[Timesheet Debug] API response counts', {
          submitted: submitted.length,
          approved: approved.length,
          returned: returned.length
        });
      }

      /*
       * Admins see everything. A manager sees their CURRENT team — not whoever the timesheet
       * was addressed to when its draft was created.
       *
       * ── What this replaces, and the three bugs it was causing ─────────────────────────
       *
       * This used to be `t.managerId.toLowerCase().trim() === user.email.toLowerCase().trim()`:
       * raw equality against a string frozen onto the row by createWeeklyTimesheet and never
       * updated again. Three separate ways for a timesheet to reach nobody:
       *
       *   1. DOMAIN. `anji@creyentetech.com` and `anji@creyenteinfotech.com` are the same person
       *      — identity.ts declares them aliases and canonicalises to the latter — but string
       *      equality says otherwise, so a manager signing in through SSO matched nothing that
       *      had been stored under the other domain.
       *   2. MANAGER CHANGED. The stamp is taken when the DRAFT is created, so moving somebody
       *      to a new manager left every existing week addressed to the old one, forever.
       *   3. EMPTY. Rows exist with `managerId: ""`. Nothing could ever equal them, so they were
       *      invisible to every manager while looking perfectly normal to an admin.
       *
       * Resolving against the live reporting line fixes all three at once and needs no data
       * migration, because it is decided at read time. `managerId` is still written and still
       * worth keeping — it records who the submission was addressed to at the time, which is an
       * audit fact — it just no longer decides who can see the row.
       */
      const isAdmin = isAdminRole;

      const all = [...submitted, ...approved, ...returned]
        .filter(t => {
          if (isAdmin) return true;
          const owner = canonicalizeEmail(t.employeeId);
          const matches = Boolean(owner) && reportEmails.has(owner);

          if (DEBUG && !matches) {
            console.log('[Timesheet Debug] Record excluded', {
              status: t.status,
              weekStartDate: t.weekStartDate,
              employeeId: t.employeeId,
              storedManagerId: t.managerId || '(empty)',
              inCurrentTeam: matches,
              teamSize: reportEmails.size
            });
          }
          return matches;
        })
        .sort((a, b) => b.weekStartDate.localeCompare(a.weekStartDate));

      if (DEBUG) {
        console.log('[Timesheet Debug] After filter', {
          filterType: isAdmin ? 'ADMIN' : 'MANAGER',
          itemsIncluded: all.length,
          totalProcessed: submitted.length + approved.length + returned.length
        });
      }

      setTimesheets(all);
    } catch (err) {
      console.error('[TeamTimesheets] Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleExpand = async (id: string) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!entries[id]) {
      const e = await GraphQLTimesheetService.getEntriesForTimesheet(id);
      setEntries(prev => ({ ...prev, [id]: e }));
    }
  };

  const handleApprove = async (ts: WeeklyTimesheet) => {
    if (!user) return;
    setActionLoading(ts.id);
    try {
      await GraphQLTimesheetService.approveTimesheet(ts.id, user.email);
      // In-app notification
      try {
        await notifyTimesheetApproved(ts.employeeId, user.name || user.email, formatWeekLabel(ts.weekStartDate), user.email);
      } catch (e) { console.warn('[TeamTimesheets] Notification failed:', e); }
      // Email notification to employee + CC HR
      try {
        await emailTimesheetApproved(
          ts.employeeId,
          ts.employeeName || ts.employeeId,
          user.name || user.email,
          formatWeekLabel(ts.weekStartDate),
          ts.totalHours || 0
        );
      } catch (e) { console.warn('[TeamTimesheets] Email failed:', e); }
      await loadData();
    } catch { alert('Failed to approve.'); }
    finally { setActionLoading(null); }
  };

  const handleReturn = async (ts: WeeklyTimesheet) => {
    const reason = prompt('Reason for returning this timesheet:');
    if (!reason?.trim()) return;
    setActionLoading(ts.id);
    try {
      await GraphQLTimesheetService.returnTimesheet(ts.id, reason.trim());
      // In-app notification
      try {
        await notifyTimesheetReturned(ts.employeeId, user?.name || '', formatWeekLabel(ts.weekStartDate), reason.trim(), user?.email);
      } catch (e) { console.warn('[TeamTimesheets] Notification failed:', e); }
      // Email notification to employee + CC HR
      try {
        await emailTimesheetReturned(
          ts.employeeId,
          ts.employeeName || ts.employeeId,
          user?.name || user?.email || '',
          formatWeekLabel(ts.weekStartDate),
          ts.totalHours || 0,
          reason.trim()
        );
      } catch (e) { console.warn('[TeamTimesheets] Email failed:', e); }
      await loadData();
    } catch { alert('Failed to return.'); }
    finally { setActionLoading(null); }
  };

  const filtered = (() => {
    let result = filter === 'All' ? timesheets : timesheets.filter(t => t.status === filter);
    if (weekFilter !== 'all') {
      result = result.filter(t => t.weekStartDate === weekFilter);
    }
    // Filter by selected employees
    if (selectedEmployees.length > 0) {
      const selectedEmails = new Set(selectedEmployees.map(e => e.email.toLowerCase()));
      result = result.filter(t => selectedEmails.has((t.employeeId || '').toLowerCase()));
    }
    // Date range filter
    if (dateFrom) {
      result = result.filter(t => t.weekStartDate >= dateFrom);
    }
    if (dateTo) {
      result = result.filter(t => t.weekStartDate <= dateTo);
    }
    return result;
  })();
  const counts: Record<string, number> = { All: timesheets.length };
  timesheets.forEach(t => { counts[t.status] = (counts[t.status] || 0) + 1; });

  const weekOptions = getLastNWeeks(8);

  // Filter dropdown suggestions based on search query
  // allUsers is already scoped: direct reports for MANAGER, all users for ADMIN

  const dropdownPool = allUsers;

  const searchSuggestions = (() => {
    const q = searchQuery.toLowerCase().trim();
    const selectedEmails = new Set(selectedEmployees.map(e => e.email.toLowerCase()));
    let suggestions = dropdownPool.filter(e => !selectedEmails.has(e.email.toLowerCase()));
    if (q) {
      suggestions = suggestions.filter(e =>
        e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q)
      );
    }
    return suggestions;
  })();

  const handleSelectEmployee = (emp: { name: string; email: string }) => {
    setSelectedEmployees(prev => [...prev, emp]);
    setSearchQuery('');
    setSearchFocused(false);
  };

  const handleRemoveEmployee = (email: string) => {
    setSelectedEmployees(prev => prev.filter(e => e.email !== email));
  };

  return (
    <section className="tms-team-page">
      <div className="tms-team-header">
        <h2 className="tms-team-title">Team Timesheets</h2>
      </div>

      <div className="tms-team-filters">
        {FILTERS.map(f => (
          <button key={f.key} className={`tms-team-filter ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label} <span className="tms-team-filter-badge">{counts[f.key] || 0}</span>
          </button>
        ))}
        <select className="tms-team-week-filter" value={weekFilter} onChange={e => setWeekFilter(e.target.value)}>
          <option value="all">All Weeks</option>
          {weekOptions.map(week => (
            <option key={week.startDate} value={week.startDate}>{week.relativeLabel}: {week.label}</option>
          ))}
        </select>
      </div>

      <div className="tms-team-search-row">
        <div className="tms-team-search-container">
          {selectedEmployees.length > 0 && (
            <div className="tms-team-search-tags">
              {selectedEmployees.map(emp => (
                <span key={emp.email} className="tms-team-search-tag">
                  {emp.name}
                  <button className="tms-team-search-tag__remove" onClick={() => handleRemoveEmployee(emp.email)}>×</button>
                </span>
              ))}
            </div>
          )}
          <input
            type="text"
            className="tms-team-search-input"
            placeholder="Search employees..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
          />
          {searchFocused && searchSuggestions.length > 0 && (
            <div className="tms-team-search-dropdown">
              {searchSuggestions.slice(0, 10).map(emp => (
                <button
                  key={emp.email}
                  className="tms-team-search-dropdown__item"
                  onMouseDown={() => handleSelectEmployee(emp)}
                >
                  <span className="tms-team-search-dropdown__name">{emp.name}</span>
                  <span className="tms-team-search-dropdown__email">{emp.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="tms-team-date-range">
          <label className="tms-team-date-label">From:</label>
          <input type="date" className="tms-team-date-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <label className="tms-team-date-label">To:</label>
          <input type="date" className="tms-team-date-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) && (
            <button className="tms-team-date-clear" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
          )}
        </div>
      </div>

      {/*
        * A directory failure is reported, never rendered as an empty team.
        *
        * Scoping the table through the reporting line means an unreadable directory yields zero
        * rows, which is indistinguishable from "nobody submitted anything" unless it is said out
        * loud. A manager who believes the empty state stops chasing their team.
        */}
      {directoryFailed && !loading && (
        <div className="tt-notice tt-notice--error" role="alert">
          The employee directory could not be loaded, so your team could not be resolved. Any
          timesheets below are incomplete &mdash; reload the page before acting on them.
        </div>
      )}

      {loading ? <InlineLoader message="Loading team timesheets" /> : filtered.length === 0 ? (
        <EmptyState icon="inbox" title="No team timesheets" subtitle="Your team members haven't submitted any timesheets yet." />
      ) : (
        <div className="tms-team-list">
          {filtered.map(ts => (
            <div key={ts.id} className="tms-team-card">
              <div className="tms-team-card__header" onClick={() => handleExpand(ts.id)}>
                <div className="tms-team-card__info">
                  <span className="tms-team-card__name">{ts.employeeName}</span>
                  <span className="tms-team-card__week">{ts.weekStartDate} — {ts.weekEndDate}</span>
                  <span className="tms-team-card__hours">{ts.totalHours}h</span>
                </div>
                <div className="tms-team-card__right">
                  <span className={`tms-status-badge tms-status-${ts.status.toLowerCase()}`}>{ts.status}</span>
                  <span className="tms-team-card__chevron">{expandedId === ts.id ? '▾' : '▸'}</span>
                </div>
              </div>

              {expandedId === ts.id && (
                <div className="tms-team-card__detail">
                  {entries[ts.id] ? (
                    entries[ts.id].length === 0 ? <p className="tms-team-card__empty">No entries</p> : (
                      <table className="tms-team-detail-table">
                        <thead><tr><th>Date</th><th>Project</th><th>Task</th><th>Hours</th><th>Notes</th></tr></thead>
                        <tbody>
                          {entries[ts.id].sort((a, b) => a.date.localeCompare(b.date)).map(e => (
                            <tr key={e.id}><td>{e.date}</td><td>{e.projectName}</td><td>{e.taskName}</td><td>{e.hours}</td><td>{e.description || '—'}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  ) : <p>Loading entries...</p>}

                  {ts.status === TimesheetStatus.SUBMITTED && (
                    <div className="tms-team-card__actions">
                      <button className="tms-btn tms-btn-primary" onClick={() => handleApprove(ts)} disabled={actionLoading === ts.id}>
                        {actionLoading === ts.id ? 'Processing...' : '✓ Approve'}
                      </button>
                      <button className="tms-btn tms-btn-outline" onClick={() => handleReturn(ts)} disabled={actionLoading === ts.id}>
                        ↩ Return
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default TeamTimesheets;
